import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { EASE } from "@/components/motion/Reveal";
import { cn } from "@/lib/utils";

const BASE_LINKS = [
  { to: "/models", label: "Models" },
  { to: "/offers", label: "Offers", offers: true },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = ({ showOffers = false }) => {
  const LINKS = BASE_LINKS.filter((l) => !l.offers || showOffers);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
        className={cn("fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500", scrolled || open ? "glass" : "border-b border-transparent")}
      >
        <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Logo />
          <nav className="hidden items-center gap-10 lg:flex">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`} className={({ isActive }) => cn("nav-link", isActive && "active text-white")}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/test-drive" data-testid="nav-test-drive-button" className="btn-crimson hidden sm:inline-flex !py-2.5 !px-5">
              Book a Test Drive
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-white/50 lg:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col justify-end bg-obsidian/95 px-6 pb-16 pt-28 backdrop-blur-xl lg:hidden"
          >
            <div className="grid-texture absolute inset-0 opacity-60" />
            <nav className="relative flex flex-col gap-2">
              {[{ to: "/", label: "Home" }, ...LINKS, { to: "/test-drive", label: "Test Drive" }].map((l, i) => (
                <motion.div key={l.to} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.08 + i * 0.06, duration: 0.7, ease: EASE }}>
                  <Link to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-baseline gap-4 border-b border-white/10 py-4">
                    <span className="font-mono text-xs text-crimson">0{i + 1}</span>
                    <span className="font-display text-4xl font-bold uppercase tracking-tight text-white">{l.label}</span>
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
